import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Shield, Globe, Award, Sparkles, Cpu, Map, Sprout, CheckCircle2 } from 'lucide-react';
import SEO from '@/components/global/SEO';
import PageTransition from '@/components/global/PageTransition';
import { Language } from '@/types';

interface AboutPageProps {
  lang: Language;
}

const AboutPage: React.FC<AboutPageProps> = ({ lang }) => {
  const isAr = lang === 'ar';
  const isRtl = isAr;

  return (
    <PageTransition>
      <div
        className={`min-h-screen bg-[#05070A] text-[#EDF0F1] selection:bg-[#0E93A3]/30 ${isAr ? 'font-arabic' : 'font-sans'
          } antialiased`}
        dir={isAr ? 'rtl' : 'ltr'}
        style={{ fontFeatureSettings: '"ss01" on' }}
      >
        <SEO
          title={
            isAr
              ? 'عن فكسل — مختبرات الذكاء المكاني والأنظمة الجيومكانية'
              : 'About VEXL — Spatial Intelligence & GeoAI Labs'
          }
          description={
            isAr
              ? 'فكسل هي شركة أبحاث ونشر في مجال الذكاء الاصطناعي الجيومكاني. نبني أنظمة ذكاء مكاني موثوقة وقابلة للتفسير وسيادية.'
              : 'VEXL is a GeoAI research and deployment company. We build reliable, interpretable, and sovereign spatial intelligence systems.'
          }
          lang={lang}
          breadcrumbs={[
            { name: isAr ? 'الرئيسية' : 'Home', item: '/' },
            { name: isAr ? 'عن فكسل' : 'About VEXL', item: '/about' }
          ]}
        />

        {/* ═══════════════════════════════════════════════════════════════════════
            SECTION 1 — HERO STATEMENT (Matches Home.tsx Hero Container & Height)
        ═══════════════════════════════════════════════════════════════════════ */}
        <section className="relative bg-[#05070A] min-h-[50vh] md:min-h-[55vh] flex items-center overflow-hidden px-6 z-20 pt-20">
          <div
            className={`
              w-full max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-start
              ${isRtl ? 'text-right' : 'text-left'}
            `}
          >
            {/* Left Column (Main Headline) */}
            <div className="lg:col-span-7">
              <h1
                className="text-3xl md:text-5xl lg:text-[52px] font-medium text-[#EDF0F1] tracking-[-0.5px] max-w-2xl leading-[1.12]"
                style={{
                  fontFamily: isAr
                    ? "'Noto Sans Arabic', 'Cairo', system-ui, -apple-system, sans-serif"
                    : "'IBM Plex Sans Condensed', 'Inter', system-ui, sans-serif",
                  lineHeight: isAr ? 1.35 : 1.12,
                }}
              >
                {isAr
                  ? 'نجعل الذكاء المكاني متاحاً لكل صانع قرار.'
                  : 'Making spatial intelligence accessible to every decision\u2011maker.'}
              </h1>
            </div>

            {/* Right Column (Sub-Description) */}
            <div className="lg:col-span-5">
              <p className="text-[#9AA5AC] text-base md:text-[17px] leading-[1.6] max-w-lg lg:mt-2 font-normal">
                {isAr
                  ? 'فكسل هي شركة أبحاث ونشر وتطوير في مجال الذكاء الاصطناعي الجيومكاني. نصمم ونبني أنظمة ذكاء مكاني موثوقة وقابلة للتفسير وسيادية لمعالجة أكثر التحديات تعقيداً على كوكب الأرض.'
                  : 'VEXL is a GeoAI research, deployment, and systems engineering company. We build reliable, interpretable, and sovereign spatial intelligence platforms to model the physical world.'}
              </p>
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════════════════
            SECTION 1.5 — HERO FEATURED VISUAL (Matches Home.tsx Section 2 Mockup Frame)
        ═══════════════════════════════════════════════════════════════════════ */}
        <section className="pt-2 pb-8 md:pb-12 bg-[#05070A] px-6 relative z-20">
          <div className="max-w-[1400px] mx-auto">
            <div className="w-full rounded-xl overflow-hidden border border-[#262C31] bg-[#12161A] shadow-sm">
              <img
                src="/images/about/about-hero.webp"
                alt={isAr ? 'فريق ومختبرات فكسل' : 'VEXL Spatial AI Labs in action'}
                className="w-full h-auto max-h-[560px] object-cover select-none pointer-events-none"
                loading="lazy"
                draggable={false}
                onError={(e) => {
                  const target = e.currentTarget;
                  target.style.display = 'none';
                  const parent = target.parentElement;
                  if (parent && !parent.querySelector('.fallback-placeholder')) {
                    const placeholder = document.createElement('div');
                    placeholder.className = 'fallback-placeholder w-full h-[280px] md:h-[420px] flex flex-col items-center justify-center text-center p-6 text-slate-400 font-mono text-xs';
                    placeholder.innerHTML = `<span class="text-[#0E93A3] text-sm font-bold mb-2">VEXL LABS HQ</span><span>about-hero.webp — 1400×560px</span>`;
                    parent.appendChild(placeholder);
                  }
                }}
              />
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════════════════
            SECTION 2 — OUR PURPOSE (3 Pillars Grid)
        ═══════════════════════════════════════════════════════════════════════ */}
        <section className="pt-10 pb-12 md:pt-14 md:pb-16 bg-[#05070A] px-6 relative z-20 border-t border-[#262C31]">
          <div className="max-w-[1400px] mx-auto">

            <div className="max-w-3xl mb-12">
              <span className="text-[#0E93A3] text-[11px] font-bold font-mono uppercase tracking-[0.22em]">
                {isAr ? 'الركائز والغايات' : 'Our Purpose & Core Mission'}
              </span>
              <h2
                className="mt-3 text-3xl md:text-[40px] font-medium text-[#EDF0F1] tracking-[-0.8px] leading-[1.18]"
                style={{ fontFamily: "'IBM Plex Sans Condensed', 'Inter', sans-serif" }}
              >
                {isAr ? 'لماذا نبني فكسل؟' : 'Why We Build VEXL'}
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
              {[
                {
                  num: '01',
                  titleEn: 'Bridging Vectors & Pixels',
                  titleAr: 'جسر يربط بين الفيكتور والبيكسل',
                  descEn: 'VEXL is named after the fundamental convergence of Vectors and Pixels. We unite discrete GIS geometries with continuous multi-spectral satellite imagery into one reliable, sovereign spatial reasoning system.',
                  descAr: 'اسم فكسل مستوحى من الدمج الرياضي بين الفيكتور والبيكسل. نوحد معالم وحدود نظم المعلومات الجغرافية (Vector) مع بيانات وصور الأقمار الصناعية متعددة الأطياف (Pixel) في نظام ذكاء مكاني سيادي وموثوق.',
                },
                {
                  num: '02',
                  titleEn: 'Safety & Rigor Is Engineering',
                  titleAr: 'الدقة المكانية أمان ومسؤولية',
                  descEn: 'We treat spatial accuracy as a safety-critical discipline. Our models are verified, our pipelines auditable, and our outputs fully deterministic and explainable.',
                  descAr: 'نتعامل مع الدقة المكانية كتخصص أمان حرج. نماذجنا محققة علمياً، خطوط بياناتنا قابلة للتدقيق الكامل، ومخرجاتنا تخلو من الهلوسة والافتراضات غير المبررة.',
                },
                {
                  num: '03',
                  titleEn: 'Built from Egypt, for the World',
                  titleAr: 'من مصر، ننطلق إلى العالم',
                  descEn: 'Headquartered in Cairo, we are building infrastructure-grade GeoAI tools engineered specifically for complex geographies, arid zones, and emerging economies.',
                  descAr: 'مقرنا في القاهرة، ونبني أدوات ذكاء جغرافي على مستوى البنية التحتية تعمل بكفاءة عبر الأسواق الناشئة والمناطق الجافة والجغرافيات المعقدة حول العالم.',
                },
              ].map((pillar) => (
                <div
                  key={pillar.num}
                  className="rounded-xl bg-[#12161A] border border-[#262C31] p-7 md:p-8 flex flex-col justify-between hover:border-[#0E93A3] transition-all duration-300 shadow-sm"
                >
                  <div>
                    <span className="text-xs font-mono font-bold text-[#0E93A3] uppercase tracking-wider mb-4 block">
                      {pillar.num} //
                    </span>
                    <h3 className="text-xl md:text-[22px] font-medium text-[#EDF0F1] tracking-tight mb-3">
                      {isAr ? pillar.titleAr : pillar.titleEn}
                    </h3>
                    <p className="text-sm leading-[1.65] text-[#9AA5AC] font-normal">
                      {isAr ? pillar.descAr : pillar.descEn}
                    </p>
                  </div>
                </div>
              ))}
            </div>

          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════════════════
            SECTION 3 — THE TEAM & DISCIPLINES
        ═══════════════════════════════════════════════════════════════════════ */}
        <section className="pt-10 pb-12 md:pt-14 md:pb-16 bg-[#05070A] px-6 relative z-20 border-t border-[#262C31]">
          <div className="max-w-[1400px] mx-auto">

            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
              <div className="max-w-3xl">
                <span className="text-[#0E93A3] text-[11px] font-bold font-mono uppercase tracking-[0.22em]">
                  {isAr ? 'الفريق والتخصصات' : 'The Team & Disciplines'}
                </span>
                <h2
                  className="mt-3 text-3xl md:text-[40px] font-medium text-[#EDF0F1] tracking-[-0.8px] leading-[1.18]"
                  style={{ fontFamily: "'IBM Plex Sans Condensed', 'Inter', sans-serif" }}
                >
                  {isAr ? 'عقول تبني طبقة البنية التحتية المكانية' : 'Engineering the Spatial Infrastructure Layer'}
                </h2>
              </div>
              <p className="max-w-md text-[#9AA5AC] text-sm md:text-[15px] leading-[1.6] font-normal">
                {isAr
                  ? 'نحن فريق متكامل يضم باحثين في علوم الذكاء الاصطناعي، ومهندسي بيانات مكانية، ومطوري أنظمة يعملون معاً لإعادة تعريف تفاعل الإنسان مع الخرائط.'
                  : 'We are researchers, spatial data scientists, and systems engineers building production-grade intelligence tools for real-world operations.'}
              </p>
            </div>

            {/* Team Photo Grid — 4 Columns on desktop */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5 mb-14">
              {[
                { src: '/images/about/about-team-1.webp', alt: isAr ? 'فريق فكسل — مختبر الأبحاث' : 'VEXL Team — Research Lab', tag: 'RESEARCH' },
                { src: '/images/about/about-team-2.webp', alt: isAr ? 'فريق فكسل — هندسة النظم' : 'VEXL Team — Systems Engineering', tag: 'ENGINEERING' },
                { src: '/images/about/about-team-3.webp', alt: isAr ? 'فريق فكسل — تطوير المنتجات' : 'VEXL Team — Product Studio', tag: 'PRODUCT' },
                { src: '/images/about/about-team-4.webp', alt: isAr ? 'فريق فكسل — العمليات والسيادة' : 'VEXL Team — Operations', tag: 'OPERATIONS' },
              ].map((photo, idx) => (
                <div
                  key={idx}
                  className="aspect-[4/3] rounded-xl overflow-hidden bg-[#12161A] border border-[#262C31] relative group"
                >
                  <img
                    src={photo.src}
                    alt={photo.alt}
                    className="w-full h-full object-cover select-none pointer-events-none"
                    loading="lazy"
                    draggable={false}
                    onError={(e) => {
                      const target = e.currentTarget;
                      target.style.display = 'none';
                      const parent = target.parentElement;
                      if (parent && !parent.querySelector('.fallback-placeholder')) {
                        const placeholder = document.createElement('div');
                        placeholder.className = 'fallback-placeholder w-full h-full flex flex-col items-center justify-center p-4 text-center text-slate-400 font-mono text-[11px]';
                        placeholder.innerHTML = `<span class="text-[#0E93A3] font-bold mb-1">${photo.tag}</span><span>640×480px</span>`;
                        parent.appendChild(placeholder);
                      }
                    }}
                  />
                  <div className="absolute top-3 left-3 bg-black/60 backdrop-blur-md px-2 py-0.5 rounded text-[10px] font-mono text-white/80 border border-white/10">
                    {photo.tag}
                  </div>
                </div>
              ))}
            </div>

            {/* Department Descriptions Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {[
                {
                  titleEn: 'Research & Labs',
                  titleAr: 'الأبحاث والمختبرات',
                  descEn: 'Frontier spatial AI research — from satellite time-series forecasting to GeoAI code generation and model interpretability.',
                  descAr: 'أبحاث متقدمة في الذكاء المكاني — من التنبؤ بالسلاسل الزمنية للأقمار الصناعية إلى توليد أكواد التحليل الجغرافي وتفسير النماذج.',
                },
                {
                  titleEn: 'Systems Engineering',
                  titleAr: 'هندسة النظم والبنية',
                  descEn: 'High-throughput pipelines processing terabytes of raster, vector, and point-cloud data with auditable, deterministic performance.',
                  descAr: 'أنظمة إنتاجية فائقة السرعة تعالج تيرابايتات من بيانات الراستر والمتجهات وسحب النقاط بحتمية وكفاءة تدقيق عالية.',
                },
                {
                  titleEn: 'Product Studio',
                  titleAr: 'تطوير المنتجات',
                  descEn: 'Translating research into production tools — VEXL Nova, CropLens, and DroughtWatch — trusted by real practitioners in the field.',
                  descAr: 'تحويل الأبحاث إلى أدوات إنتاجية ملموسة — VEXL Nova و CropLens و DroughtWatch — تلبي متطلبات المستخدمين الميدانيين.',
                },
                {
                  titleEn: 'Governance & IP',
                  titleAr: 'الحوكمة والملكية الفكرية',
                  descEn: 'Ensuring absolute legal compliance, data sovereignty, academic partnerships, and institutional registrations.',
                  descAr: 'ضمان الامتثال القانوني الصارم، وسيادة البيانات، وتوثيق براءات الاختراع والملكيات الفكرية المسجلة رسمياً.',
                },
              ].map((dept, idx) => (
                <div
                  key={idx}
                  className="rounded-xl bg-[#12161A] border border-[#262C31] p-6 flex flex-col justify-between"
                >
                  <div>
                    <h4 className="text-base font-semibold text-[#EDF0F1] tracking-tight mb-2.5">
                      {isAr ? dept.titleAr : dept.titleEn}
                    </h4>
                    <p className="text-xs md:text-sm leading-[1.65] text-[#9AA5AC] font-normal">
                      {isAr ? dept.descAr : dept.descEn}
                    </p>
                  </div>
                </div>
              ))}
            </div>

          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════════════════
            SECTION 4 — OUR VALUES & PRINCIPLES
        ═══════════════════════════════════════════════════════════════════════ */}
        <section className="pt-10 pb-12 md:pt-14 md:pb-16 bg-[#0B0F12] px-6 relative z-20 border-t border-[#262C31]">
          <div className="max-w-[1400px] mx-auto">

            <div className="max-w-3xl mb-12">
              <span className="text-[#0E93A3] text-[11px] font-bold font-mono uppercase tracking-[0.22em]">
                {isAr ? 'القيم والمعايير الهندسية' : 'Engineering Values & Principles'}
              </span>
              <h2
                className="mt-3 text-3xl md:text-[40px] font-medium text-[#EDF0F1] tracking-[-0.8px] leading-[1.18]"
                style={{ fontFamily: "'IBM Plex Sans Condensed', 'Inter', sans-serif" }}
              >
                {isAr ? 'ما نؤمن به وكيف نعمل كل يوم' : 'What We Value and How We Act'}
              </h2>
            </div>

            <div className="divide-y divide-[#262C31] border-y border-[#262C31]">
              {[
                {
                  num: '01',
                  titleEn: 'Spatial Truth over Assumptions.',
                  titleAr: 'الحقيقة المكانية فوق الافتراضات.',
                  descEn: 'Every model we build is grounded in verifiable geographic data. We do not guess — we measure, validate, and explain. When a critical decision depends on location, the data must be rigorously accurate, and the reasoning transparent.',
                  descAr: 'كل نموذج نبنيه يرتكز على بيانات جغرافية قابلة للتحقق الهندسي. لا نعتمد على التخمين — بل نقيس وندقق ونشرح. عندما يرتبط القرار بموقع حيوي، يجب أن تكون البيانات صادقة والمنهجية واضحة للجميع.',
                },
                {
                  num: '02',
                  titleEn: 'Sovereignty by Design.',
                  titleAr: 'السيادة والملكية الكاملة بالتصميم.',
                  descEn: 'Our software codebase, analytical pipelines, and neural network architectures are proprietary. We engineer robust spatial pipelines ensuring institutional data security and operational sovereignty.',
                  descAr: 'شفرتنا البرمجية ومنظوماتنا التحليلية ومعمارياتنا العصبية مملوكة ومطورة ذاتياً. نصمم مسارات مكانية تضمن أمن البيانات الحساسة والاستقلالية التشغيلية للمؤسسات.',
                },
                {
                  num: '03',
                  titleEn: 'Build for the Hardest Geography First.',
                  titleAr: 'ابنِ للجغرافيا الأكثر تعقيداً أولاً.',
                  descEn: 'If an AI model can resolve complex agricultural parcels in the Nile Delta and hyper-arid desert boundaries, it can succeed anywhere. We engineer for data-scarce, challenging environments, not idealized lab setups.',
                  descAr: 'إذا أثبت النموذج كفاءته في تداخل الحيازات الزراعية بدلتا النيل وحدود الصحاري الجافة، فسينجح في أي بقعة حول العالم. نصمم لظروف الواقع المعقدة، وليس للافتراضات النظرية في المعامل.',
                },
                {
                  num: '04',
                  titleEn: 'Ship Production Tools, Not Pitch Demos.',
                  titleAr: 'أطلق أدوات إنتاجية حقيقية، وليس عروضاً دعائية.',
                  descEn: 'We do not build flimsy prototypes for presentation slides. Every tool must deploy into production, withstand real workloads, and parse millions of spatial records without degradation.',
                  descAr: 'لا نضيع الوقت في نماذج دعائية للمؤتمرات. كل منتج نصدره يجب أن يكون جاهزاً للعمل في بيئة الإنتاج الفعلية، ويتحمل ضغط ملايين السجلات الجغرافية بكفاءة تامة.',
                },
                {
                  num: '05',
                  titleEn: 'Explain Every Coordinate & Risk Score.',
                  titleAr: 'اشرح كل إحداثية وكل درجة مخاطر.',
                  descEn: 'Black-box AI has no place in national infrastructure or food security decisions. If our systems compute a risk index or damage metric, the scientific derivation must be auditable line by line.',
                  descAr: 'لا مكان للذكاء الاصطناعي المبهم (الصندوق الأسود) في قرارات البنية التحتية والأمن الغذائي. كل مخرج تحليلي أو تقييم مخاطر تصدره فكسل يجب أن يكون مدعماً بتفسير علمي قابل للمراجعة والتدقيق.',
                },
              ].map((val) => (
                <div key={val.num} className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8 py-8 md:py-10 items-start">
                  <div className="md:col-span-2 text-2xl md:text-3xl font-mono text-[#0E93A3] font-light">
                    {val.num}
                  </div>
                  <div className="md:col-span-10 space-y-3">
                    <h3 className="text-xl md:text-2xl font-medium text-[#EDF0F1] tracking-tight">
                      {isAr ? val.titleAr : val.titleEn}
                    </h3>
                    <p className="text-sm md:text-[15px] text-[#9AA5AC] leading-[1.7] font-normal">
                      {isAr ? val.descAr : val.descEn}
                    </p>
                  </div>
                </div>
              ))}
            </div>

          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════════════════
            SECTION 5 — FOUNDER & ORIGIN
        ═══════════════════════════════════════════════════════════════════════ */}
        <section className="pt-10 pb-12 md:pt-14 md:pb-16 bg-[#05070A] px-6 relative z-20 border-t border-[#262C31]">
          <div className="max-w-[1400px] mx-auto">

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 items-start">

              {/* Founder Image Card */}
              <div className="lg:col-span-4">
                <div className="aspect-[4/5] rounded-xl overflow-hidden bg-[#12161A] border border-[#262C31] shadow-sm">
                  <img
                    src="/images/about/about-founder.webp"
                    alt={isAr ? 'عبدالرحمن ضياء — مؤسس فكسل' : 'Abdelrahman Diaa — Founder of VEXL'}
                    className="w-full h-full object-cover select-none pointer-events-none"
                    loading="lazy"
                    draggable={false}
                    onError={(e) => {
                      const target = e.currentTarget;
                      target.style.display = 'none';
                      const parent = target.parentElement;
                      if (parent && !parent.querySelector('.fallback-placeholder')) {
                        const placeholder = document.createElement('div');
                        placeholder.className = 'fallback-placeholder w-full h-full flex flex-col items-center justify-center p-6 text-center text-slate-400 font-mono text-xs';
                        placeholder.innerHTML = `<span class="text-[#0E93A3] font-bold text-sm mb-2">FOUNDER</span><span>about-founder.webp — 400×500px</span>`;
                        parent.appendChild(placeholder);
                      }
                    }}
                  />
                </div>
              </div>

              {/* Founder Narrative */}
              <div className="lg:col-span-8 space-y-6 pt-2">
                <div>
                  <span className="text-[#0E93A3] text-[11px] font-bold font-mono uppercase tracking-[0.22em] mb-2 block">
                    {isAr ? 'المؤسس والرؤية' : 'Founder & Technical Vision'}
                  </span>
                  <h2
                    className="text-3xl md:text-[38px] font-medium text-[#EDF0F1] tracking-[-0.8px] leading-[1.18]"
                    style={{ fontFamily: "'IBM Plex Sans Condensed', 'Inter', sans-serif" }}
                  >
                    {isAr ? 'عبدالرحمن ضياء' : 'Abdelrahman Diaa'}
                  </h2>
                  <p className="text-sm font-medium text-[#0E93A3] mt-1 font-mono">
                    {isAr ? 'المؤسس ورئيس الأبحاث والمعمارية — فكسل' : 'Founder & Chief AI Architect — VEXL'}
                  </p>
                </div>

                <p className="text-base leading-[1.75] text-[#9AA5AC] font-normal">
                  {isAr
                    ? 'باحث ومتخصص في نظم المعلومات الجغرافية والاستشعار عن بعد بجامعة القاهرة. قاد تصميم وهندسة منظومة VEXL بالكامل كأصل برمجي جيومكاني مسجل رسمياً برقم إيداع (5007) لدى الجهاز المصري للملكية الفكرية. تُوّج مشروعه البحثي DroughtWatch بالمركز الثالث عالمياً في تحدي GESC 2026 التابع لمعهد برنامج الأمم المتحدة للبيئة بجامعة تونغجي (UNEP-Tongji).'
                    : 'A spatial data researcher and GIS engineer from Cairo University who architected the entire VEXL platform as a sovereign software asset registered under Egyptian Intellectual Property Authority Deposit No. 5007. Awarded 3rd place globally at the prestigious GESC 2026 Challenge by the UNEP-Tongji Institute for deep learning drought forecasting.'}
                </p>

                {/* Quote Box with Teal Border */}
                <div className="p-6 rounded-xl bg-[#12161A] border-l-4 rtl:border-l-0 rtl:border-r-4 border-[#0E93A3] border-y border-r rtl:border-r-0 rtl:border-l border-[#262C31]">
                  <p className="text-base md:text-lg italic text-[#EDF0F1] leading-relaxed">
                    {isAr
                      ? '«تأسست فكسل لتكون الصوت الهندسي الصارم في عالم الذكاء المكاني — نرفض أن تُعامل الخريطة كخلفية شكلية، بل نبنيها لتكون العقل المركزي لصنع القرار السيادي.»'
                      : '"VEXL was created to turn spatial data into an uncompromising decision engine — geography is not a decorative background, but the physical foundation of truth."'}
                  </p>
                </div>

                {/* Quick Credentials Pills */}
                <div className="flex flex-wrap items-center gap-3 pt-2">
                  <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-[#262C31] bg-[#12161A] text-xs text-[#9AA5AC]">
                    <Award size={14} className="text-[#0E93A3]" />
                    <span>UNEP-Tongji GESC 2026 Laureate</span>
                  </div>
                  <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-[#262C31] bg-[#12161A] text-xs text-[#9AA5AC]">
                    <Shield size={14} className="text-[#639922]" />
                    <span>EGIPA Deposit #5007</span>
                  </div>
                </div>

              </div>

            </div>

          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════════════════
            SECTION 6 — MILESTONES & RECOGNITION BAR
        ═══════════════════════════════════════════════════════════════════════ */}
        <section className="py-10 md:py-12 bg-[#05070A] px-6 relative z-20 border-t border-[#262C31]">
          <div className="max-w-[1400px] mx-auto">

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
              {[
                {
                  metricEn: '3rd Place Globally',
                  metricAr: 'المركز الـ 3 عالمياً',
                  labelEn: 'GESC 2026 (UNEP-Tongji)',
                  labelAr: 'تحدي GESC 2026 الدولي',
                },
                {
                  metricEn: 'EGIPA Deposit #5007',
                  metricAr: 'إيداع رسمي #5007',
                  labelEn: 'Registered Software IP',
                  labelAr: 'ملكية فكرية مسجلة ومحمية',
                },
                {
                  metricEn: '4 Live Platforms',
                  metricAr: '4 منصات تخصصية',
                  labelEn: 'Nova, GeoAgent, CropLens, DroughtWatch',
                  labelAr: 'نوفا، جيو إيجنت، كروب لينس، دراوت ووتش',
                },
                {
                  metricEn: 'Cairo, Egypt',
                  metricAr: 'القاهرة، مصر',
                  labelEn: 'HQ & Research Hub',
                  labelAr: 'المقر والمختبرات المركزية',
                },
              ].map((stat, idx) => (
                <div
                  key={idx}
                  className="p-5 rounded-xl bg-[#12161A] border border-[#262C31] flex flex-col justify-between"
                >
                  <span className="text-lg md:text-xl font-bold text-[#EDF0F1] tracking-tight">
                    {isAr ? stat.metricAr : stat.metricEn}
                  </span>
                  <span className="text-xs text-[#9AA5AC] font-mono mt-2 block">
                    {isAr ? stat.labelAr : stat.labelEn}
                  </span>
                </div>
              ))}
            </div>

          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════════════════
            SECTION 7 — STRATEGIC CTA CARD (Matches Home.tsx CTA Style)
        ═══════════════════════════════════════════════════════════════════════ */}
        <section className="bg-[#05070A] pt-8 pb-14 md:pt-10 md:pb-20 px-6 relative z-20">
          <div className="max-w-5xl mx-auto">
            <div className="bg-[#12161A] rounded-xl p-10 md:p-16 border border-[#262C31] shadow-sm">

              <div className="text-center max-w-2xl mx-auto">
                <span className="text-[#0E93A3] text-[11px] font-bold font-mono uppercase tracking-[0.22em] mb-4 block">
                  {isAr ? 'انضم إلى المنظومة' : 'Join the Mission'}
                </span>
                <h2
                  className="text-3xl md:text-[40px] font-medium text-[#EDF0F1] tracking-[-0.84px] mb-6 leading-[1.15]"
                  style={{ fontFamily: "'IBM Plex Sans Condensed', 'Inter', sans-serif" }}
                >
                  {isAr
                    ? 'هل تود بناء مستقبل الذكاء المكاني معنا؟'
                    : 'Ready to build the spatial intelligence layer?'}
                </h2>
                <p className="text-sm md:text-base text-[#9AA5AC] leading-[1.65] font-normal mb-8">
                  {isAr
                    ? 'نحن نرحب دائماً بالباحثين ومهندسي الذكاء المكاني والشركاء المؤسسيين الحريصين على تحقيق الاستقلالية والسيادة الجغرافية.'
                    : 'We are actively collaborating with researchers, spatial engineers, and enterprise partners who share our commitment to spatial truth and sovereignty.'}
                </p>
                <div className="flex flex-wrap justify-center items-center gap-4">
                  <Link
                    to="/careers"
                    className="px-6 py-3 rounded-sm bg-[#0E93A3] hover:bg-[#075C68] text-white font-medium text-sm tracking-wide transition-all duration-200 inline-flex items-center gap-1.5"
                  >
                    <span>{isAr ? 'استعرض الوظائف والأبحاث' : 'View Open Careers'}</span>
                    <ArrowRight size={16} className={isAr ? 'rotate-180' : ''} />
                  </Link>
                  <Link
                    to="/contact"
                    className="px-6 py-3 rounded-sm border border-[#39424A] bg-transparent hover:bg-[#171C21] hover:border-[#0E93A3] text-[#EDF0F1] font-medium text-sm transition-all duration-200 inline-flex items-center gap-1.5"
                  >
                    <span>{isAr ? 'تواصل مع القيادة' : 'Contact Leadership'}</span>
                  </Link>
                </div>
              </div>

            </div>
          </div>
        </section>

      </div>
    </PageTransition>
  );
};

export default AboutPage;

